import BookerSelect from "@/components/BookerSelect";
import CurrencySelect from "@/components/CurrencySelect";
import DataLoading from "@/components/DataLoading";
import { FormFieldShell } from "@/components/forms/FormField";
import { LogoIcon } from "@/components/LogoIcon";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { CampaignType, Client, Currency, Platform, User } from "@/shared/types";

const PLATFORMS: Platform[] = ["TikTok", "Instagram", "YouTube"];

export type TalentBrowseCampaignConstraintsProps = {
    booker?: { isManager: boolean; value?: User; bookers: User[]; clients: Client[]; onSelect: (booker: User | undefined) => void; disabled: boolean };
    currency: { value: Currency | string | null; locked: boolean; disabled: boolean; onChange: (value: string | null) => void };
    platforms: { value: Platform[]; disabled?: boolean; required?: boolean; tooltip?: string; onToggle: (platform: Platform, checked: boolean) => void };
    campaignType?: { value?: CampaignType; locked?: boolean; disabled: boolean; onChange: (type: CampaignType) => void };
};

type PlatformCheckboxesProps = {
    platforms: Platform[];
    disabled?: boolean;
    onToggle: (platform: Platform, checked: boolean) => void;
};

function PlatformCheckboxes({ platforms, disabled = false, onToggle }: PlatformCheckboxesProps) {
    return (
        <div className="flex flex-wrap items-center gap-3">
            {PLATFORMS.map((platform) => (
                <label
                    key={platform}
                    className={`flex items-center gap-2 ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                >
                    <Checkbox
                        checked={platforms.includes(platform)}
                        disabled={disabled}
                        onCheckedChange={(checked) => onToggle(platform, checked === true)}
                        aria-label={platform}
                    />
                    <LogoIcon brand={platform} className="size-9" title={platform} />
                </label>
            ))}
        </div>
    );
}

/**
 * Page chrome always mounted as a sibling above TalentBrowse — not rendered by TalentBrowse itself.
 * New Booking passes booker and a lock tooltip; Edit List omits both. Roster / Public List / Pitching omit this component.
 */
export function TalentBrowseCampaignConstraints({ booker, currency, platforms, campaignType }: TalentBrowseCampaignConstraintsProps) {
    const platformsDisabled = Boolean(platforms.disabled);
    const campaignTypeDisabled = Boolean(campaignType?.locked || campaignType?.disabled);

    return (
        <div className="mb-4 flex flex-wrap items-end gap-4">
            {booker?.isManager ? (
                <FormFieldShell
                    label="Select a Booker"
                    required
                    fieldClassName="min-w-64 sm:w-72"
                    htmlFor="booker-select"
                >
                    {() => (
                        booker.bookers.length && booker.clients.length ?
                            <BookerSelect
                                value={booker.value}
                                bookers={booker.bookers}
                                clients={booker.clients}
                                onBookerSelect={booker.onSelect}
                                disabled={booker.disabled}
                            />
                            : <DataLoading />
                    )}
                </FormFieldShell>
            ) : null}

            <FormFieldShell
                label="Currency"
                htmlFor="currency-select"
                disabled={currency.disabled || currency.locked}
                fieldClassName="w-auto"
            >
                {() => (
                    <CurrencySelect
                        hideLabel
                        triggerClassName="my-0"
                        disabled={currency.disabled}
                        locked={currency.locked}
                        value={currency.value}
                        onChange={currency.onChange}
                    />
                )}
            </FormFieldShell>

            <FormFieldShell
                label="Platform"
                htmlFor="browse-platforms"
                required={platforms.required}
                disabled={platformsDisabled}
                fieldClassName="w-auto"
            >
                {(controlProps) => (
                    platforms.tooltip ? (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger render={<div className="inline-block" id={controlProps.id} />}>
                                    <PlatformCheckboxes
                                        platforms={platforms.value}
                                        disabled={platformsDisabled}
                                        onToggle={platforms.onToggle}
                                    />
                                </TooltipTrigger>
                                <TooltipContent>{platforms.tooltip}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ) : (
                        <div id={controlProps.id}>
                            <PlatformCheckboxes
                                platforms={platforms.value}
                                disabled={platformsDisabled}
                                onToggle={platforms.onToggle}
                            />
                        </div>
                    )
                )}
            </FormFieldShell>

            {campaignType ? (
                <FormFieldShell
                    label="Campaign Type"
                    htmlFor="campaign-type"
                    disabled={campaignTypeDisabled}
                    fieldClassName="w-auto"
                >
                    {(controlProps) => (
                        <Select
                            value={campaignType.value || null}
                            onValueChange={(next) => {
                                if (!next) return;
                                campaignType.onChange(String(next) as CampaignType);
                            }}
                            disabled={campaignTypeDisabled}
                        >
                            <SelectTrigger id={controlProps.id} className="w-auto min-w-40 bg-white">
                                <SelectValue placeholder="Choose Type..." />
                            </SelectTrigger>
                            <SelectContent alignItemWithTrigger={false}>
                                <SelectItem value="Song">Song Promos</SelectItem>
                                <SelectItem value="Brand">Brand Promos</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                </FormFieldShell>
            ) : null}
        </div>
    );
}
